/* =====================================================================
   İstisnai süre durması dönemleri — yargısal sürelerin ülke genelinde
   veya bölgesel olarak DURDUĞU olağanüstü dönemler (COVID-19, deprem).

   SAF mantık (React'sız). Normal hesaplamayı DEĞİŞTİRMEZ; yalnızca
   bilgilendirme üretir. Hesabın aktif aralığı bir dönemle örtüşürse
   sonuç ekranında bilgilendirme kartı gösterilir.

   tur:
     'ulusal'  → ülke geneli; COVID gibi. Düzeltilmiş tarih HESAPLANIR.
     'bolgesel'→ belirli illerde; deprem gibi. Kapsam kişiye/avukata
                 bağlı olduğundan tek tarih hesaplanamaz → yalnızca BİLGİ.

   Tarihler ISO ('YYYY-MM-DD'); örtüşme/karşılaştırma sözlüksel yapılır.
   Kaynaklar: 7226 sk. Geç. m.1 + 2480 s. CK (COVID); 120 + 139 s. CBK (deprem).
   ===================================================================== */

import { addDays, sonGunHesapla, ilkIsGunu, gunFarki } from './isGunuHesap';

export const ISTISNAI_DONEMLER = [
  {
    id: 'covid-19',
    ad: 'COVID-19 süre durması',
    tur: 'ulusal',
    baslangic: '2020-03-13',
    bitis: '2020-06-15', // dahil; süreler 16/6/2020'den kaldığı yerden işledi
    onbesGunKurali: true,
    dayanak: '7226 sk. Geç. m.1 · 2480 s. CK',
    aciklama:
      'COVID-19 salgını nedeniyle yargısal süreler 13/3/2020 – 15/6/2020 arasında durdu; ' +
      '16/6/2020’den itibaren kaldığı yerden işledi. Durma başında bitimine 15 gün ve daha az ' +
      'kalan süreler, durma bitiminden başlayarak 15 gün uzamış sayıldı (son gün 30/6/2020).',
  },
  {
    id: 'deprem-2023',
    ad: '2023 depremi süre durması',
    tur: 'bolgesel',
    baslangic: '2023-02-06',
    bitis: '2023-05-01', // en geniş pencere (uzatma dahil); çekirdek durma 6/4/2023
    cekirdekBitis: '2023-04-06',
    onbesGunKurali: false,
    iller: ['Adana', 'Adıyaman', 'Diyarbakır', 'Gaziantep', 'Hatay', 'Kahramanmaraş', 'Kilis', 'Malatya', 'Osmaniye', 'Şanlıurfa'],
    dayanak: '120 s. CBK · 139 s. CBK',
    aciklama:
      'Deprem nedeniyle OHAL ilan edilen illerde yargısal süreler 6/2/2023 – 6/4/2023 (bazı il ve ' +
      'ilçelerde 1/5/2023) arasında durdu. Kapsam; bu illerde yerleşim yeri bulunan veya fiilen ' +
      'bulunan kişiler ile avukatlarına bağlıdır (ülke genelindeki dosyaları için).',
  },
];

/** [aBaslangic, aBitis] ile [bBaslangic, bBitis] kapalı aralıkları kesişiyor mu? (ISO sözlüksel) */
function araliklarKesisir(aBas, aBit, bBas, bBit) {
  return aBas <= bBit && aBit >= bBas;
}

/**
 * COVID (7226 sk.) süre durması dikkate alınarak düzeltilmiş başvuru son günü.
 * Normal hesabı değiştirmez; bilgilendirme amaçlı ikinci bir tarih üretir.
 * @returns { sonGun, kesinlesme } | null  (örtüşme yoksa null)
 */
export function covidDuzelt(tebligIso, sure, birim, diniSet, donem) {
  const W1 = donem.baslangic; // 2020-03-13
  const W2 = donem.bitis;     // 2020-06-15 (son durma günü; ertesi gün kaldığı yerden işler)
  const B = addDays(tebligIso, 1);            // sürenin 1. günü
  const naturalSon = sonGunHesapla(tebligIso, sure, birim); // doğal (uzatma öncesi) son gün
  if (!B || !naturalSon) return null;

  // Süre tamamen durmanın dışındaysa etki yok.
  if (gunFarki(naturalSon, W1) > 0) return null; // naturalSon < W1 (durmadan önce bitti)
  if (gunFarki(B, W2) < 0) return null;          // B > W2 (durmadan sonra başladı)

  let ham;
  if (gunFarki(W1, B) > 0) {
    // B durma içinde (W1 < B ≤ W2): süre 16/6'dan (W2 ertesi) işler.
    ham = sonGunHesapla(W2, sure, birim);
  } else {
    // B ≤ W1: süre durma başladığında işliyordu.
    const kalan = gunFarki(W1, naturalSon); // durma başında bitimine kalan gün
    if (donem.onbesGunKurali && kalan <= 15) {
      ham = addDays(W2, 15); // 30/6/2020
    } else if (gunFarki(naturalSon, W2) >= 0) {
      // naturalSon ≤ W2: kalan kısmın tamamı askıda → W2 ertesinden devam.
      ham = addDays(W2, kalan);
    } else {
      // naturalSon > W2: yalnızca durma penceresi kadar ötele.
      ham = addDays(naturalSon, gunFarki(W1, W2));
    }
  }

  const roll = ilkIsGunu(ham, diniSet); // son gün resmî/dinî tatil/hafta sonu ise ilk iş gününe
  return { sonGun: roll.tarih, kesinlesme: addDays(roll.tarih, 1) };
}

/**
 * Hesabın aktif aralığıyla ([baslangic, sonGun]) örtüşen istisnai dönemleri döndürür.
 * Ulusal dönemler için (COVID) `duzeltilmis: {sonGun, kesinlesme}` da hesaplanır;
 * bölgesel dönemler için (deprem) hesaplama yapılmaz, yalnızca bilgi (iller) döner.
 * @returns Array<{ id, ad, tur, dayanak, aciklama, iller?, duzeltilmis? }>
 */
export function istisnaDegerlendir({ tebligIso, baslangic, sonGun, sure, birim, diniSet }) {
  if (!baslangic || !sonGun) return [];
  const out = [];
  for (const d of ISTISNAI_DONEMLER) {
    if (!araliklarKesisir(baslangic, sonGun, d.baslangic, d.bitis)) continue;
    const kayit = { id: d.id, ad: d.ad, tur: d.tur, dayanak: d.dayanak, aciklama: d.aciklama };
    if (d.tur === 'ulusal') {
      const duzeltilmis = covidDuzelt(tebligIso, sure, birim, diniSet, d);
      if (duzeltilmis) kayit.duzeltilmis = duzeltilmis;
    } else {
      kayit.iller = d.iller;
    }
    out.push(kayit);
  }
  return out;
}
